import React, { useState, useRef, useEffect } from "react";
import { safeEvalArithmetic } from "../lib/safeEval";

export default function NumberInput({value,formula,onChange,style,onFocus:extFocus,onBlur:extBlur,onKeyDown,"data-oe-item":dataOeItem,"data-oe-field":dataOeField}:{
  value:number|null|undefined,
  formula?:string,
  onChange:(v:number,formula?:string)=>void,
  style?:any,
  onFocus?:()=>void,
  onBlur?:()=>void,
  onKeyDown?:(e:React.KeyboardEvent<HTMLInputElement>)=>void,
  "data-oe-item"?:string,
  "data-oe-field"?:string
}){
  const [editing,setEditing]=useState(false);
  const [raw,setRaw]=useState("");
  const [invalid,setInvalid]=useState(false);
  const ref=useRef<HTMLInputElement>(null);
  const invalidTimer=useRef<ReturnType<typeof setTimeout>|null>(null);
  const selectTimer=useRef<ReturnType<typeof setTimeout>|null>(null);
  useEffect(()=>()=>{
    if(invalidTimer.current)clearTimeout(invalidTimer.current);
    if(selectTimer.current)clearTimeout(selectTimer.current);
  },[]);
  const onFocus=()=>{
    const initial = formula && formula.trim()
      ? formula
      : (value!=null && value!==0 ? String(value) : "");
    setRaw(initial);
    setEditing(true);
    setInvalid(false);
  /**
   * Select-the-value runs on a timer because the value only becomes the raw
   * one after this render. The timer has to check it still belongs here:
   * select() FOCUSES a blurred input, so one left over from a cell you have
   * already moved on from drags the caret back -- and two of them take turns
   * doing it, forever. Arrow around the grid quickly and it would never stop.
   */
    if(selectTimer.current)clearTimeout(selectTimer.current);
    selectTimer.current=setTimeout(()=>{
      if(document.activeElement===ref.current)ref.current?.select();
    },0);
    extFocus?.();
  };
  const commitBlur=()=>{
    if(selectTimer.current){clearTimeout(selectTimer.current);selectTimer.current=null;}
    setEditing(false);
    const s=String(raw).trim();
    let result=0;
    let storedFormula: string | undefined;
    const evalResult = s ? safeEvalArithmetic(s) : null;
    if (evalResult !== null) { result = evalResult; storedFormula = s; }
    else {
      const parsed = parseFloat(s.replace(/[^0-9.-]/g, ""));
      // Entry couldn't be read as a number — don't zero it silently.
      // Flash the field so the user knows their value was rejected.
      if (s && (isNaN(parsed) || !isFinite(parsed))) {
        setInvalid(true);
        if(invalidTimer.current)clearTimeout(invalidTimer.current);
        invalidTimer.current=setTimeout(()=>setInvalid(false),2500);
      }
      result = parsed || 0;
      storedFormula = undefined;
    }
    onChange(isNaN(result)||!isFinite(result)?0:result, storedFormula);
    extBlur?.();
  };
  const hasFormula = !!(formula && formula.trim());
  const displayVal = editing ? raw : (value==null||value===0 ? "" : String(value));
  return <input ref={ref} className="input-field" type="text" inputMode="decimal"
    data-oe-item={dataOeItem} data-oe-field={dataOeField}
    title={invalid ? "Couldn't read that as a number — value reset to 0" : (hasFormula && !editing ? `= ${formula}` : undefined)}
    value={displayVal}
    onChange={e=>setRaw(e.target.value)} onFocus={onFocus} onBlur={commitBlur}
    onKeyDown={onKeyDown}
    style={{
      fontStyle:hasFormula&&!editing?"italic":"normal",
      ...style,
      ...(invalid?{outline:"1.5px solid #e05252",outlineOffset:-1,background:"#e0525218",transition:"background .2s"}:{}),
    }}/>;
}
